import { collection, addDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { triggerFulfillmentNotification } from './notificationTriggerService';
import { createBidsFromPortalRequest, linkBidIdsToPortalRequest } from './alaCarteBidService';

function portalRequestSummary(kind, source, details) {
  const d = details && typeof details === 'object' ? details : {};
  let summary = '—';
  if (kind === 'alacarte') {
    const n = Array.isArray(d.cart) ? d.cart.length : 0;
    summary = `À la carte · ${n} item(s)`;
  } else if (kind === 'program' && d.kind === 'additional_items_request') {
    const ids = Array.isArray(d.itemIds) ? d.itemIds.length : 0;
    summary = `Additional items · ${ids} selected`;
    if (d.estimatedMonthlyTotal != null) summary += ` · ~$${Math.round(d.estimatedMonthlyTotal)} est.`;
  } else if (kind === 'program' && d.kind === 'cancellation_request') {
    summary = `Cancellation request${d.programName ? ` · ${d.programName}` : ''}`;
    if (d.monthlyAmount != null) summary += ` · $${Number(d.monthlyAmount).toLocaleString()}/mo`;
    if (d.nextBillingDate) {
      const next = new Date(d.nextBillingDate);
      if (!Number.isNaN(next.getTime())) summary += ` · next bill ${next.toLocaleDateString()}`;
    }
  } else if (kind === 'program' && d.kind === 'plan_change') {
    summary = d.programName ? String(d.programName) : 'Plan change';
    const delta = d.deltaMonthly;
    summary += typeof delta === 'number' ? ` · Δ ${delta >= 0 ? '+' : ''}$${delta}/mo est.` : '';
    if (d.billingCycle) summary += ` · ${d.billingCycle}`;
  } else {
    summary = d.programName ? String(d.programName) : 'Program request';
    if (d.billingCycle) summary += ` · ${d.billingCycle}`;
  }
  const sourceLabel =
    source === 'customprogram'
      ? 'Custom program'
      : source === 'programdetail'
        ? 'Program detail'
        : source === 'billing'
          ? 'Portal billing'
          : source === 'alacarte'
            ? 'À la carte shop'
            : source || '—';
  return { summary, sourceLabel };
}

/**
 * Portal user requests a program package or à la carte cart — stored for CRM General Fulfillment.
 * @param {Object} opts
 * @param {'program'|'alacarte'} opts.kind
 * @param {'programdetail'|'customprogram'|'alacarte'|'billing'} opts.source
 * @param {Record<string, unknown>} opts.details - full payload for internal review
 * @param {string} opts.userId
 * @param {string} opts.userEmail
 * @param {string} [opts.accountId]
 * @param {string} [opts.requestedBy] - display name (defaults to userEmail)
 * @param {string} [opts.company]
 */
export async function submitPortalFulfillmentRequest({
  kind,
  source,
  details,
  userId,
  userEmail,
  accountId,
  requestedBy,
  company,
}) {
  const now = new Date().toISOString();
  const requestRef = await addDoc(collection(db, 'portalFulfillmentRequests'), {
    kind,
    source,
    details: details && typeof details === 'object' ? details : {},
    userId: userId || '',
    userEmail: userEmail || '',
    accountId: accountId || '',
    status: 'requested',
    bidIds: [],
    createdAt: now,
    updatedAt: now,
  });

  if (kind === 'alacarte' && accountId) {
    try {
      const bidIds = await createBidsFromPortalRequest(
        { id: requestRef.id, data: () => ({ kind, source, details, accountId }) },
        userId || ''
      );
      if (bidIds.length > 0) {
        await linkBidIdsToPortalRequest(requestRef.id, bidIds);
      }
    } catch (bidError) {
      console.error('Error creating à la carte bids from portal request:', bidError);
    }
  }

  try {
    const { summary, sourceLabel } = portalRequestSummary(kind, source, details);
    const typeLabel = kind === 'alacarte' ? 'à la carte' : 'program';
    const by = (requestedBy && String(requestedBy).trim()) || userEmail || 'Portal user';
    const co = (company && String(company).trim()) || '';
    await triggerFulfillmentNotification('general', {
      type: `portal ${typeLabel} request`,
      name: kind === 'alacarte' ? 'Portal À la Carte Request' : 'Portal Program Request',
      description: `${summary} · Source: ${sourceLabel}${accountId ? ` · Account ID: ${accountId}` : ''}`,
      requestedBy: by,
      company: co,
      timestamp: now,
    });
  } catch (notificationError) {
    console.error('Error sending general fulfillment notification for portal request:', notificationError);
  }
}
